import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from '../hooks/useBackend';

interface PersonaGroup {
  group_id: number;
  name: string;
  bias_ids: number[];
  narrative_mapping_ids: number[];
  user_count: number;
  classifier?: {
    classifier_id: number;
    classifier_type: string;
    classifier_params: Record<string, any>;
  } | null;
  users: Persona[];
}

interface Persona {
  user_id: string;
  persona_type: string;
  labels: string | null;  // Backend sends as string, not array
  demographic: string;
  groups: string[];
  region?: {
    region_id: number;
    carrier_region: string;
    carrier_name: string;
    mcc_mnc: string;
    locale: string;
    timezone_name: string;
    timezone_long_name: string;
  } | null;
  device_info?: {
    device_info_id: number;
    device_type: string;
    device_brand: string;
    device_platform: string;
    device_os_version: number;
    device_os_api: number;
    web_ua: string;
    cpu_abi: string;
    resolution_width: number;
    resolution_height: number;
    dpi: number;
  } | null;
  proxy?: {
    proxy_id: number;
    proxy_url: string;
    proxy_name?: string | null;
  } | null;
}

interface Region {
  region_id: number;
  carrier_region: string;
  carrier_name: string;
  mcc_mnc: string;
  locale: string;
  timezone_name: string;
  timezone_long_name: string;
}

interface Classifier {
  classifier_id: number;
  classifier_type: string;
  classifier_params: Record<string, any>;
}

interface DeviceInfo {
  deviceInfoId: number;
  deviceType: string;
  deviceBrand: string;
  devicePlatform: string;
  deviceOsVersion: number;
  deviceOsApi: number;
  webUa: string;
  cpuAbi: string;
  resolutionWidth: number;
  resolutionHeight: number;
  dpi: number;
}

interface Proxy {
  proxy_id: number;
  proxy_url: string;
  proxy_name?: string | null;
  personas?: string[];
}

interface Narrative {
  narrative_id: number;
  name: string;
  description?: string;
}

interface CreateGroupRequest {
  name: string;
  classifier_id?: number;
  user_ids: string[];
  proxy_id?: number;
  narrative_mapping_ids?: number[];
}

interface CreatePersonaRequest {
  user_id?: string;
  persona_type: string;
  labels: string[];
  demographic?: string;
  region_id?: number;
  device_info_id?: number;
  proxy_id?: number;
}

interface PersonaModalProps {
  persona?: Persona;
  isOpen: boolean;
  onClose: () => void;
  onSave: (persona: CreatePersonaRequest) => void;
  isLoading: boolean;
  regions: Region[];
  devices: DeviceInfo[];
  proxies: Proxy[];
}

interface OnboardingConfig {
  group_id: number;
  genders: string[] | null;
  age_ranges: string[] | null;
  ethnicities: string[] | null;
  primary_languages: string[] | null;
  supported_applications: string[] | null;
  completion_redirect_url: string | null;
}

interface OnboardingConfigFormData {
  enabled: boolean;
  genders: string[];
  age_ranges: string[];
  ethnicities: string[];
  primary_languages: string[];
  supported_applications: string[];
  completion_redirect_url: string;
}

interface GroupModalProps {
  group?: PersonaGroup;
  onboardingConfig?: OnboardingConfig | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (group: CreateGroupRequest, onboarding: OnboardingConfigFormData) => void;
  isLoading: boolean;
  classifiers: Classifier[];
  availablePersonas: Persona[];
  proxies: Proxy[];
  narratives: Narrative[];
  onEditPersona?: (persona: Persona) => void;
}

// Helper function to parse labels string into array
const parseLabels = (labels: string | null | undefined): string[] => {
  if (!labels) return [];
  if (typeof labels === 'string') {
    // Handle both comma-separated and JSON array strings
    try {
      const parsed = JSON.parse(labels);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // If not JSON, split by comma
      return labels.split(',').map(l => l.trim()).filter(l => l);
    }
  }
  return [];
};

// Helper function to convert country code to flag emoji
// Uses Unicode regional indicator symbols (works for any 2-letter country code)
const countryCodeToFlag = (countryCode: string | undefined | null): string => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const code = countryCode.toUpperCase();
  const offset = 0x1F1E6; // Regional indicator symbol letter A
  const flag = String.fromCodePoint(
    code.charCodeAt(0) - 65 + offset,
    code.charCodeAt(1) - 65 + offset
  );
  return flag;
};

const getProxyOptionLabel = (proxy: Proxy): string => {
  const label = proxy.proxy_name || (() => {
    const match = proxy.proxy_url.match(/-zone-([^:]+)/);
    return match ? match[1] : proxy.proxy_url;
  })();
  const ccMatch = label.match(/^([a-z]{2})_proxy_\d+$/);
  const flag = ccMatch ? countryCodeToFlag(ccMatch[1]) : '';
  return flag ? `${flag} ${label}` : label;
};

const TagInput: React.FC<{
  label: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}> = ({ label, tags, onChange, placeholder }) => {
  const [input, setInput] = useState('');
  const addTag = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) {
      onChange([...tags, val]);
    }
    setInput('');
  };
  return (
    <div style={{ marginBottom: '4px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
      <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder || `Add ${label.toLowerCase()}...`}
          onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          style={{
            flex: 1,
            padding: '7px 10px',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)',
            color: 'white',
            fontSize: '13px',
          }}
        />
        <button type="button" onClick={addTag} style={{
          padding: '7px 14px',
          borderRadius: '6px',
          border: '1px solid rgba(99,102,241,0.4)',
          background: 'rgba(99,102,241,0.15)',
          color: '#a5b4fc',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
        }}>+</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '24px' }}>
        {tags.map((tag, i) => (
          <span key={i} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            borderRadius: '14px',
            background: 'rgba(99,102,241,0.15)',
            border: '1px solid rgba(99,102,241,0.3)',
            color: '#c7d2fe',
            fontSize: '12px',
          }}>
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((_, j) => j !== i))} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: '0 2px', fontSize: '14px', lineHeight: 1,
            }}>×</button>
          </span>
        ))}
        {tags.length === 0 && <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>None added</span>}
      </div>
    </div>
  );
};

const PersonaModal: React.FC<PersonaModalProps> = ({ persona, isOpen, onClose, onSave, isLoading, regions, devices, proxies }) => {
  const [formData, setFormData] = useState<CreatePersonaRequest>(() => ({
    user_id: persona?.user_id || '',
    persona_type: persona?.persona_type || 'bot',
    labels: parseLabels(persona?.labels),
    demographic: persona?.demographic || '',
    region_id: persona?.region?.region_id || undefined,
    device_info_id: persona?.device_info?.device_info_id || undefined,
    proxy_id: persona?.proxy?.proxy_id || undefined,
  }));

  // Update form data when persona prop changes (for edit mode)
  React.useEffect(() => {
    if (persona) {
      setFormData({
        user_id: persona.user_id,
        persona_type: persona.persona_type,
        labels: parseLabels(persona.labels),
        demographic: persona.demographic || '',
        region_id: persona.region?.region_id || undefined,
        device_info_id: persona.device_info?.device_info_id || undefined,
        proxy_id: persona?.proxy?.proxy_id || undefined,
      });
    }
  }, [persona]);

  const [labelInput, setLabelInput] = useState('');

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const addLabel = () => {
    if (labelInput.trim() && !formData.labels.includes(labelInput.trim())) {
      setFormData(prev => ({ ...prev, labels: [...prev.labels, labelInput.trim()] }));
      setLabelInput('');
    }
  };

  const removeLabel = (label: string) => {
    setFormData(prev => ({ ...prev, labels: prev.labels.filter(l => l !== label) }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{persona ? 'Edit Persona' : 'Create New Persona'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="persona-form">
          <div className="form-group">
            <label htmlFor="user_id">User ID *</label>
            <input
              id="user_id"
              type="text"
              value={formData.user_id}
              onChange={(e) => setFormData(prev => ({ ...prev, user_id: e.target.value }))}
              required
              placeholder="Enter user ID..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="persona_type">Persona Type *</label>
            <select
              id="persona_type"
              value={formData.persona_type}
              onChange={(e) => setFormData(prev => ({ ...prev, persona_type: e.target.value }))}
              required
            >
              <option value="bot">Bot</option>
              <option value="human">Human</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="demographic">Demographic</label>
            <input
              id="demographic"
              type="text"
              value={formData.demographic}
              onChange={(e) => setFormData(prev => ({ ...prev, demographic: e.target.value }))}
              placeholder="Enter demographic info..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="region">Region</label>
            <select
              id="region"
              value={formData.region_id || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, region_id: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">No region assigned</option>
              {regions.map((region) => (
                <option key={region.region_id} value={region.region_id}>
                  {region.carrier_region} - {region.carrier_name} - {region.timezone_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="device">Device</label>
            <select
              id="device"
              value={formData.device_info_id || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, device_info_id: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">No device assigned</option>
              {devices.map((device) => (
                <option key={device.deviceInfoId} value={device.deviceInfoId}>
                  {device.deviceBrand} {device.deviceType} - {device.devicePlatform} v{device.deviceOsVersion}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="proxy">Proxy</label>
            <select
              id="proxy"
              value={formData.proxy_id || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, proxy_id: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">No proxy assigned</option>
              {proxies.map((proxy) => (
                <option key={proxy.proxy_id} value={proxy.proxy_id}>
                  {getProxyOptionLabel(proxy)} (ID: {proxy.proxy_id})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Labels</label>
            <div className="labels-input">
              <div className="label-add">
                <input
                  type="text"
                  value={labelInput}
                  onChange={(e) => setLabelInput(e.target.value)}
                  placeholder="Add a label..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLabel())}
                />
                <button type="button" onClick={addLabel} className="btn-add-label">+</button>
              </div>
              <div className="labels-list">
                {formData.labels.map((label, index) => (
                  <span key={index} className="label-tag">
                    {label}
                    <button type="button" onClick={() => removeLabel(label)} className="label-remove">×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (persona ? 'Update Persona' : 'Create Persona')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const GroupModal: React.FC<GroupModalProps> = ({ group, onboardingConfig, isOpen, onClose, onSave, isLoading, classifiers, availablePersonas, proxies, narratives, onEditPersona }) => {
  const [formData, setFormData] = useState<CreateGroupRequest>(() => ({
    name: group?.name || '',
    classifier_id: group?.classifier?.classifier_id || undefined,
    user_ids: group?.users.map(u => u.user_id) || [],
    proxy_id: group?.users[0]?.proxy?.proxy_id || undefined,
    narrative_mapping_ids: group?.narrative_mapping_ids || [],
  }));

  const [onboardingData, setOnboardingData] = useState<OnboardingConfigFormData>(() => ({
    enabled: !!onboardingConfig,
    genders: onboardingConfig?.genders || [],
    age_ranges: onboardingConfig?.age_ranges || [],
    ethnicities: onboardingConfig?.ethnicities || [],
    primary_languages: onboardingConfig?.primary_languages || [],
    supported_applications: onboardingConfig?.supported_applications || [],
    completion_redirect_url: onboardingConfig?.completion_redirect_url || '',
  }));

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [personaFilter, setPersonaFilter] = useState('');
  const [showAddPersonas, setShowAddPersonas] = useState(false);
  const isEditMode = !!group;

  const { data: availableApps = [] } = useQuery<{ package_name: string; display_name: string }[]>({
    queryKey: ['app-config-package-names'],
    queryFn: async () => {
      const response = await fetch('/api/app-configurations');
      if (!response.ok) return [];
      const configs: { package_name: string; package_display_name: string }[] = await response.json();
      const seen = new Set<string>();
      const result: { package_name: string; display_name: string }[] = [];
      for (const c of configs) {
        if (!seen.has(c.package_name)) {
          seen.add(c.package_name);
          result.push({ package_name: c.package_name, display_name: c.package_display_name || c.package_name });
        }
      }
      return result.sort((a, b) => a.display_name.localeCompare(b.display_name));
    },
  });

  React.useEffect(() => {
    if (group) {
      setFormData({
        name: group.name,
        classifier_id: group.classifier?.classifier_id || undefined,
        user_ids: group.users.map(u => u.user_id),
        proxy_id: group.users[0]?.proxy?.proxy_id || undefined,
        narrative_mapping_ids: group.narrative_mapping_ids || [],
      });
    } else {
      setFormData({
        name: '',
        classifier_id: undefined,
        user_ids: [],
        proxy_id: undefined,
        narrative_mapping_ids: [],
      });
    }
    setOnboardingData({
      enabled: !!onboardingConfig,
      genders: onboardingConfig?.genders || [],
      age_ranges: onboardingConfig?.age_ranges || [],
      ethnicities: onboardingConfig?.ethnicities || [],
      primary_languages: onboardingConfig?.primary_languages || [],
      supported_applications: onboardingConfig?.supported_applications || [],
      completion_redirect_url: onboardingConfig?.completion_redirect_url || '',
    });
    setShowOnboarding(false);
    setPersonaFilter('');
    setShowAddPersonas(!group);
  }, [group, onboardingConfig]);

  // Separate selected and unselected personas
  const selectedPersonas = availablePersonas.filter(p => formData.user_ids.includes(p.user_id));
  const unselectedPersonas = availablePersonas.filter(p => !formData.user_ids.includes(p.user_id));

  // Filter personas based on search (only for unselected when adding)
  const filteredUnselectedPersonas = unselectedPersonas.filter(persona => {
    if (!personaFilter) return true;
    
    const searchTerm = personaFilter.toLowerCase();
    const personaLabels = parseLabels(persona.labels);
    
    return (
      persona.user_id.toLowerCase().includes(searchTerm) ||
      persona.persona_type.toLowerCase().includes(searchTerm) ||
      persona.demographic?.toLowerCase().includes(searchTerm) ||
      persona.region?.carrier_region.toLowerCase().includes(searchTerm) ||
      persona.region?.carrier_name.toLowerCase().includes(searchTerm) ||
      persona.region?.locale.toLowerCase().includes(searchTerm) ||
      personaLabels.some(label => label.toLowerCase().includes(searchTerm))
    );
  });

  // For create mode, filter all personas
  const filteredPersonas = availablePersonas.filter(persona => {
    if (!personaFilter) return true;
    
    const searchTerm = personaFilter.toLowerCase();
    const personaLabels = parseLabels(persona.labels);
    
    return (
      persona.user_id.toLowerCase().includes(searchTerm) ||
      persona.persona_type.toLowerCase().includes(searchTerm) ||
      persona.demographic?.toLowerCase().includes(searchTerm) ||
      persona.region?.carrier_region.toLowerCase().includes(searchTerm) ||
      persona.region?.carrier_name.toLowerCase().includes(searchTerm) ||
      persona.region?.locale.toLowerCase().includes(searchTerm) ||
      personaLabels.some(label => label.toLowerCase().includes(searchTerm))
    );
  });

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData, onboardingData);
  };

  const togglePersona = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId)
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{group ? 'Edit Persona Group' : 'Create New Persona Group'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="group-form">
          <div className="form-group">
                    <label htmlFor="group_name">Group Name *</label>
        <input
          id="group_name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="Enter group name..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="classifier">Classifier</label>
            <select
              id="classifier"
              value={formData.classifier_id || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, classifier_id: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">No classifier assigned</option>
              {classifiers.map((classifier) => (
                <option key={classifier.classifier_id} value={classifier.classifier_id}>
                  {classifier.classifier_type} - {classifier.classifier_params ? `(${JSON.stringify(classifier.classifier_params)})` : ''} (ID: {classifier.classifier_id})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="proxy">Proxy (applies to all personas in group)</label>
            <select
              id="proxy"
              value={formData.proxy_id || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, proxy_id: e.target.value ? Number(e.target.value) : undefined }))}
            >
              <option value="">No proxy assigned</option>
              {proxies.map((proxy) => (
                <option key={proxy.proxy_id} value={proxy.proxy_id}>
                  {getProxyOptionLabel(proxy)} (ID: {proxy.proxy_id})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Narrative Mappings ({(formData.narrative_mapping_ids || []).length} selected)</label>
            <div className="narratives-selection">
              {narratives.map((narrative) => {
                const isSelected = (formData.narrative_mapping_ids || []).includes(narrative.narrative_id);
                return (
                  <div
                    key={narrative.narrative_id}
                    className={`narrative-checkbox-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      const current = formData.narrative_mapping_ids || [];
                      const updated = isSelected
                        ? current.filter(id => id !== narrative.narrative_id)
                        : [...current, narrative.narrative_id];
                      setFormData(prev => ({ ...prev, narrative_mapping_ids: updated }));
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}} // Handled by parent div
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="narrative-checkbox-label">
                      <strong>
                        {narrative.name}
                        <span className="narrative-id">ID: {narrative.narrative_id}</span>
                      </strong>
                      {narrative.description && (
                        <div className="narrative-desc">{narrative.description}</div>
                      )}
                    </div>
                  </div>
                );
              })}
              {narratives.length === 0 && (
                <div className="no-narratives">
                  <p>No narratives available. Create narratives in the Narratives tab.</p>
                </div>
              )}
            </div>
          </div>

          <div style={{
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.12)',
            overflow: 'hidden',
            marginBottom: '8px',
          }}>
            <button
              type="button"
              onClick={() => setShowOnboarding(!showOnboarding)}
              style={{
                width: '100%',
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.04)',
                border: 'none',
                color: 'rgba(255,255,255,0.9)',
                fontSize: '13px',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                letterSpacing: '0.3px',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '10px', opacity: 0.5, transition: 'transform 0.2s', transform: showOnboarding ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>&#9654;</span>
                Onboarding Configuration
              </span>
              <span style={{
                fontSize: '11px',
                padding: '2px 10px',
                borderRadius: '10px',
                background: onboardingData.enabled ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)',
                color: onboardingData.enabled ? '#4ade80' : 'rgba(255,255,255,0.4)',
                border: `1px solid ${onboardingData.enabled ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.1)'}`,
                fontWeight: 500,
              }}>
                {onboardingData.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </button>

            {showOnboarding && (
              <div style={{
                padding: '16px 20px 20px',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'rgba(255,255,255,0.85)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: onboardingData.enabled ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${onboardingData.enabled ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)'}`,
                  transition: 'all 0.15s',
                }}>
                  <input
                    type="checkbox"
                    checked={onboardingData.enabled}
                    onChange={(e) => setOnboardingData(prev => ({ ...prev, enabled: e.target.checked }))}
                    style={{ accentColor: '#4ade80' }}
                  />
                  Enable onboarding for this group
                </label>

                {onboardingData.enabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Supported Applications
                      </label>
                      {availableApps.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {availableApps.map(app => {
                            const isSelected = onboardingData.supported_applications.includes(app.package_name);
                            return (
                              <label key={app.package_name} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px 12px',
                                borderRadius: '8px',
                                background: isSelected ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.02)',
                                border: `1px solid ${isSelected ? 'rgba(99,102,241,0.3)' : 'rgba(255,255,255,0.08)'}`,
                                cursor: 'pointer',
                                fontSize: '13px',
                                color: isSelected ? '#c7d2fe' : 'rgba(255,255,255,0.6)',
                                transition: 'all 0.15s',
                              }}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    const apps = isSelected
                                      ? onboardingData.supported_applications.filter(a => a !== app.package_name)
                                      : [...onboardingData.supported_applications, app.package_name];
                                    setOnboardingData(prev => ({ ...prev, supported_applications: apps }));
                                  }}
                                  style={{ accentColor: '#6366f1' }}
                                />
                                <span>{app.display_name}</span>
                                <span style={{ fontFamily: 'monospace', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>{app.package_name}</span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', padding: '8px 0' }}>
                          No app configurations defined. Add them in the App Configs tab first.
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Completion Redirect URL
                      </label>
                      <input
                        type="url"
                        value={onboardingData.completion_redirect_url}
                        onChange={(e) => setOnboardingData(prev => ({ ...prev, completion_redirect_url: e.target.value }))}
                        placeholder="https://example.com/onboarding-complete"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid rgba(255,255,255,0.12)',
                          background: 'rgba(255,255,255,0.04)',
                          color: 'rgba(255,255,255,0.9)',
                          fontSize: '13px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' }}>
                        URL to redirect users to after completing onboarding
                      </div>
                    </div>

                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '4px' }}>
                      Onboarding questions are now managed in the Onboarding tab under Configuration.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Personas ({formData.user_ids.length} selected)</span>
            </label>

            {/* Edit Mode: Show selected personas first */}
            {isEditMode && (
              <>
                {/* Selected Personas Section */}
                <div style={{ 
                  marginBottom: '16px', 
                  padding: '16px', 
                  background: 'rgba(99, 102, 241, 0.1)', 
                  borderRadius: '12px',
                  border: '1px solid rgba(99, 102, 241, 0.3)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontWeight: '600', color: '#a5b4fc' }}>
                      ✅ Selected Personas ({selectedPersonas.length})
                    </span>
                  </div>
                  
                  {selectedPersonas.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>
                      No personas selected yet
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '10px', 
                      maxHeight: '350px', 
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {selectedPersonas.map((persona) => {
                        const personaLabels = parseLabels(persona.labels);
                        return (
                          <div
                            key={persona.user_id}
                            style={{
                              background: '#1a1a1a',
                              border: '1px solid rgba(99, 102, 241, 0.4)',
                              borderRadius: '8px',
                              padding: '10px',
                              fontSize: '12px'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>{persona.persona_type === 'bot' ? '🤖' : '👤'}</span>
                                {persona.region && (
                                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                                    {countryCodeToFlag(persona.region.carrier_region)} {persona.region.carrier_region}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {onEditPersona && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEditPersona(persona);
                                    }}
                                    style={{
                                      background: 'rgba(99, 102, 241, 0.2)',
                                      border: '1px solid rgba(99, 102, 241, 0.4)',
                                      color: '#a5b4fc',
                                      borderRadius: '4px',
                                      padding: '2px 6px',
                                      cursor: 'pointer',
                                      fontSize: '10px'
                                    }}
                                    title="Edit persona"
                                  >
                                    ✏️
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => togglePersona(persona.user_id)}
                                  style={{
                                    background: 'rgba(239, 68, 68, 0.2)',
                                    border: '1px solid rgba(239, 68, 68, 0.4)',
                                    color: '#f87171',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    cursor: 'pointer',
                                    fontSize: '10px'
                                  }}
                                  title="Remove from group"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                            
                            <div style={{ 
                              color: '#a5b4fc', 
                              fontSize: '10px', 
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              marginBottom: '4px'
                            }}>
                              {persona.user_id}
                            </div>

                            {personaLabels.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                {personaLabels.slice(0, 2).map((label, index) => (
                                  <span key={index} style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    padding: '1px 4px',
                                    borderRadius: '3px',
                                    fontSize: '9px',
                                    color: 'rgba(255,255,255,0.7)'
                                  }}>{label}</span>
                                ))}
                                {personaLabels.length > 2 && (
                                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>+{personaLabels.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Add More Personas Toggle */}
                <button
                  type="button"
                  onClick={() => setShowAddPersonas(!showAddPersonas)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginBottom: '12px',
                    background: showAddPersonas ? 'rgba(99, 102, 241, 0.2)' : 'rgba(34, 197, 94, 0.1)',
                    border: `2px dashed ${showAddPersonas ? 'rgba(99, 102, 241, 0.4)' : 'rgba(34, 197, 94, 0.4)'}`,
                    borderRadius: '8px',
                    color: showAddPersonas ? '#a5b4fc' : '#4ade80',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {showAddPersonas ? '▲ Hide Available Personas' : `▼ Add More Personas (${unselectedPersonas.length} available)`}
                </button>

                {/* Available Personas Section (collapsible) */}
                {showAddPersonas && (
                  <div style={{ 
                    padding: '16px', 
                    background: 'rgba(255,255,255,0.05)', 
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div className="persona-search" style={{ marginBottom: '12px' }}>
                      <input
                        type="text"
                        placeholder="🔍 Search available personas..."
                        value={personaFilter}
                        onChange={(e) => setPersonaFilter(e.target.value)}
                        className="persona-filter-input"
                      />
                      {personaFilter && (
                        <div className="search-results-info">
                          Found {filteredUnselectedPersonas.length} personas
                          <button 
                            type="button" 
                            onClick={() => setPersonaFilter('')}
                            className="clear-search"
                          >
                            ✕ Clear
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '10px', 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {filteredUnselectedPersonas.map((persona) => {
                        const personaLabels = parseLabels(persona.labels);
                        return (
                          <div
                            key={persona.user_id}
                            onClick={() => togglePersona(persona.user_id)}
                            style={{
                              background: '#1a1a1a',
                              border: '1px solid rgba(255,255,255,0.2)',
                              borderRadius: '8px',
                              padding: '10px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              transition: 'border-color 0.2s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.borderColor = 'rgba(34, 197, 94, 0.6)'}
                            onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span>{persona.persona_type === 'bot' ? '🤖' : '👤'}</span>
                                {persona.region && (
                                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>
                                    {countryCodeToFlag(persona.region.carrier_region)} {persona.region.carrier_region}
                                  </span>
                                )}
                              </div>
                              <span style={{
                                background: 'rgba(34, 197, 94, 0.2)',
                                border: '1px solid rgba(34, 197, 94, 0.4)',
                                color: '#4ade80',
                                borderRadius: '4px',
                                padding: '2px 6px',
                                fontSize: '10px'
                              }}>
                                + Add
                              </span>
                            </div>
                            
                            <div style={{ 
                              color: 'rgba(255,255,255,0.8)', 
                              fontSize: '10px', 
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              marginBottom: '4px'
                            }}>
                              {persona.user_id}
                            </div>

                            {personaLabels.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                {personaLabels.slice(0, 2).map((label, index) => (
                                  <span key={index} style={{
                                    background: 'rgba(255,255,255,0.1)',
                                    padding: '1px 4px',
                                    borderRadius: '3px',
                                    fontSize: '9px',
                                    color: 'rgba(255,255,255,0.7)'
                                  }}>{label}</span>
                                ))}
                                {personaLabels.length > 2 && (
                                  <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>+{personaLabels.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                      
                    {filteredUnselectedPersonas.length === 0 && unselectedPersonas.length > 0 && (
                      <div className="no-personas">
                        <p>No personas match your search criteria.</p>
                      </div>
                    )}
                    
                    {unselectedPersonas.length === 0 && (
                      <div className="no-personas">
                        <p>All available personas are already in this group.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Create Mode: Show all personas with checkboxes */}
            {!isEditMode && (
              <>
                <div className="persona-search">
                  <input
                    type="text"
                    placeholder="🔍 Search personas by ID, type, region, labels, or demographic..."
                    value={personaFilter}
                    onChange={(e) => setPersonaFilter(e.target.value)}
                    className="persona-filter-input"
                  />
                  {personaFilter && (
                    <div className="search-results-info">
                      Found {filteredPersonas.length} of {availablePersonas.length} personas
                      <button 
                        type="button" 
                        onClick={() => setPersonaFilter('')}
                        className="clear-search"
                      >
                        ✕ Clear
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="personas-selection">
                  {filteredPersonas.map((persona) => {
                    const personaLabels = parseLabels(persona.labels);
                    const isSelected = formData.user_ids.includes(persona.user_id);
                    
                    return (
                      <div
                        key={persona.user_id}
                        className={`persona-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => togglePersona(persona.user_id)}
                      >
                        <div className="persona-card-header">
                          <div className="persona-main-info">
                            <div className="persona-type-badge">
                              {persona.persona_type === 'bot' ? '🤖' : '👤'} {persona.persona_type}
                            </div>
                            {persona.region && (
                              <div className="persona-region-info">
                                <span className="region-flag">
                                  {countryCodeToFlag(persona.region.carrier_region)}
                                </span>
                                <span className="region-name">{persona.region.carrier_region}</span>
                                <span className="carrier-name">({persona.region.carrier_name})</span>
                              </div>
                            )}
                          </div>
                          <div className="persona-actions">
                            {onEditPersona && (
                              <button
                                type="button"
                                className="edit-persona-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditPersona(persona);
                                }}
                                title="Edit persona"
                              >
                                ✏️
                              </button>
                            )}
                            <div className="selection-indicator">
                              {isSelected ? '✅' : '⭕'}
                            </div>
                          </div>
                        </div>
                        
                        {persona.demographic && (
                          <div className="persona-demographic">
                            <strong>Demo:</strong> {persona.demographic}
                          </div>
                        )}

                        {persona.device_info && (
                          <div className="persona-device-info">
                            <strong>Device:</strong> {persona.device_info.device_type} {persona.device_info.device_brand} {persona.device_info.device_platform} v{persona.device_info.device_os_version}
                          </div>
                        )}

                        {personaLabels.length > 0 && (
                          <div className="persona-labels">
                            <strong>Labels:</strong>
                            <div className="labels-list">
                              {personaLabels.slice(0, 4).map((label, index) => (
                                <span key={index} className="label-tag mini">{label}</span>
                              ))}
                              {personaLabels.length > 4 && (
                                <span className="label-more">+{personaLabels.length - 4}</span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="persona-id">
                          <small>ID: {persona.user_id.slice(0, 8)}...</small>
                        </div>
                      </div>
                    );
                  })}
                  
                  {filteredPersonas.length === 0 && availablePersonas.length > 0 && (
                    <div className="no-personas">
                      <p>No personas match your search criteria.</p>
                      <small>Try adjusting your search or clearing the filter.</small>
                    </div>
                  )}
                  
                  {availablePersonas.length === 0 && (
                    <div className="no-personas">
                      <p>No available personas to assign.</p>
                      <small>All personas are already assigned to other groups.</small>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : (group ? 'Update Group' : 'Create Group')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const PersonaGroups: React.FC = () => {
  const queryClient = useQueryClient();
  const backend = useBackend();
  
  const [selectedGroup, setSelectedGroup] = useState<PersonaGroup | undefined>();
  const [selectedPersona, setSelectedPersona] = useState<Persona | undefined>();
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [groupSearchFilter, setGroupSearchFilter] = useState('');

  // Fetch data
  const { data: groups = [], isLoading: groupsLoading, error: groupsError, refetch: refetchGroups } = useQuery<PersonaGroup[]>({
    queryKey: ['persona-groups'],
    queryFn: async () => {
      const response = await fetch('/api/persona-groups?include_users=true&include_regions=true&include_device=true&include_proxy=true&include_classifier=true');
      if (!response.ok) throw new Error('Failed to fetch groups');
      return response.json();
    },
    refetchInterval: 30000,
  });

  const { data: allPersonas = [] } = useQuery<Persona[]>({
    queryKey: ['personas'],
    queryFn: async () => {
      const response = await fetch('/api/personas?include_groups=true&include_region=true&include_device=true&include_proxy=true');
      if (!response.ok) throw new Error('Failed to fetch personas');
      return response.json();
    },
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ['regions'],
    queryFn: async () => {
      const response = await fetch('/api/regions');
      if (!response.ok) throw new Error('Failed to fetch regions');
      return response.json();
    },
  });

  const { data: classifiers = [] } = useQuery<Classifier[]>({
    queryKey: ['classifiers'],
    queryFn: async () => {
      const response = await fetch('/api/classifiers');
      if (!response.ok) throw new Error('Failed to fetch classifiers');
      return response.json();
    },
  });

  const { data: devices = [] } = useQuery<DeviceInfo[]>({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await fetch('/api/device-infos');
      if (!response.ok) throw new Error('Failed to fetch devices');
      const backendData = await response.json();
      
      // Transform snake_case to camelCase for frontend
      return backendData.map((device: any) => ({
        deviceInfoId: device.device_info_id,
        deviceType: device.device_type,
        deviceBrand: device.device_brand,
        devicePlatform: device.device_platform,
        deviceOsVersion: device.device_os_version,
        deviceOsApi: device.device_os_api,
        webUa: device.web_ua,
        cpuAbi: device.cpu_abi,
        resolutionWidth: device.resolution_width,
        resolutionHeight: device.resolution_height,
        dpi: device.dpi
      }));
    },
  });

  const { data: proxies = [] } = useQuery<Proxy[]>({
    queryKey: ['proxies'],
    queryFn: async () => {
      const response = await fetch('/api/proxies');
      if (!response.ok) throw new Error('Failed to fetch proxies');
      return response.json();
    },
  });

  const { data: narratives = [] } = useQuery<Narrative[]>({
    queryKey: ['narratives'],
    queryFn: async () => {
      const response = await fetch('/api/narratives');
      if (!response.ok) throw new Error('Failed to fetch narratives');
      return response.json();
    },
  });

  const [onboardingConfigs, setOnboardingConfigs] = useState<Record<number, OnboardingConfig>>({});
  const [selectedOnboardingConfig, setSelectedOnboardingConfig] = useState<OnboardingConfig | null>(null);

  const { data: appDisplayNames = {} } = useQuery<Record<string, string>>({
    queryKey: ['app-display-names'],
    queryFn: async () => {
      const response = await fetch('/api/app-configurations');
      if (!response.ok) return {};
      const configs: { package_name: string; package_display_name: string }[] = await response.json();
      const map: Record<string, string> = {};
      for (const c of configs) {
        if (!map[c.package_name]) map[c.package_name] = c.package_display_name || c.package_name;
      }
      return map;
    },
  });

  React.useEffect(() => {
    if (groups.length === 0) return;
    const fetchConfigs = async () => {
      const configs: Record<number, OnboardingConfig> = {};
      await Promise.all(
        groups.map(async (group) => {
          try {
            const response = await fetch(`/api/onboarding/${group.group_id}/configuration`);
            if (response.ok) {
              configs[group.group_id] = await response.json();
            }
          } catch { /* no config for this group */ }
        })
      );
      setOnboardingConfigs(configs);
    };
    fetchConfigs();
  }, [groups]);

  // Get personas not in any group
  const unassignedPersonas = allPersonas.filter(persona => 
    !groups.some(group => group.users.some(user => user.user_id === persona.user_id))
  );

  const availablePersonas = [...unassignedPersonas, ...(selectedGroup?.users || [])];

  // Sort groups by creation time (newest first) and filter by search
  const filteredAndSortedGroups = groups
    .filter(group => 
      !groupSearchFilter || 
      group.name.toLowerCase().includes(groupSearchFilter.toLowerCase())
    )
    .sort((a, b) => b.group_id - a.group_id); // group_id is auto-increment, so higher = newer

  // Mutations
  const createGroupMutation = useMutation({
    mutationFn: async (groupData: CreateGroupRequest) => {
      const response = await fetch('/api/persona-groups', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(groupData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create group');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsGroupModalOpen(false);
      setSelectedGroup(undefined);
      queryClient.invalidateQueries({ queryKey: ['persona-groups'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updateGroupMutation = useMutation({
    mutationFn: async ({ groupId, groupData }: { groupId: number; groupData: CreateGroupRequest }) => {
      const response = await fetch(`/api/persona-groups/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(groupData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update group');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsGroupModalOpen(false);
      setSelectedGroup(undefined);
      queryClient.invalidateQueries({ queryKey: ['persona-groups'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const createPersonaMutation = useMutation({
    mutationFn: async (personaData: CreatePersonaRequest) => {
      const response = await fetch('/api/personas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(personaData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create persona');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsPersonaModalOpen(false);
      setSelectedPersona(undefined);
      queryClient.invalidateQueries({ queryKey: ['personas'] });
      queryClient.invalidateQueries({ queryKey: ['persona-groups'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const updatePersonaMutation = useMutation({
    mutationFn: async ({ userId, personaData }: { userId: string; personaData: CreatePersonaRequest }) => {
      const response = await fetch(`/api/personas/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(personaData),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update persona');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      setIsPersonaModalOpen(false);
      setSelectedPersona(undefined);
      queryClient.invalidateQueries({ queryKey: ['personas'] });
      queryClient.invalidateQueries({ queryKey: ['persona-groups'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (groupId: number) => {
      const response = await fetch(`/api/persona-groups/${groupId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete group');
      }
      return response.json();
    },
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['persona-groups'] });
      queryClient.invalidateQueries({ queryKey: ['personas'] });
    },
    onError: (error: Error) => setActionError(error.message),
  });

  // Event handlers
  const handleCreateGroup = useCallback(() => {
    setSelectedGroup(undefined);
    setSelectedOnboardingConfig(null);
    setIsGroupModalOpen(true);
    setActionError(null);
  }, []);

  const handleEditGroup = useCallback((group: PersonaGroup) => {
    setSelectedGroup(group);
    setSelectedOnboardingConfig(onboardingConfigs[group.group_id] || null);
    setIsGroupModalOpen(true);
    setActionError(null);
  }, [onboardingConfigs]);

  const handleCreatePersona = useCallback(() => {
    setSelectedPersona(undefined);
    setIsPersonaModalOpen(true);
    setActionError(null);
  }, []);

  const handleEditPersona = useCallback((persona: Persona) => {
    // Find the full persona data from allPersonas to ensure we have complete region/device info
    const fullPersona = allPersonas.find(p => p.user_id === persona.user_id) || persona;
    setSelectedPersona(fullPersona);
    setIsPersonaModalOpen(true);
    setActionError(null);
  }, [allPersonas]);

  const handleDeleteGroup = useCallback((group: PersonaGroup) => {
    if (window.confirm(`Are you sure you want to delete the group "${group.name}"?\n\nThis will remove the group but keep all personas. This action cannot be undone.`)) {
      deleteGroupMutation.mutate(group.group_id);
    }
  }, [deleteGroupMutation]);

  const handleAssignPersonaToGroup = useCallback(async (personaId: string, groupId: number) => {
    try {
      setActionError(null);
      
      // Find the group to update
      const group = groups.find(g => g.group_id === groupId);
      if (!group) {
        throw new Error('Group not found');
      }
      
      // Add the persona to the group's user list
      const updatedUserIds = [...group.users.map(u => u.user_id), personaId];
      
      const updateData = {
        name: group.name,
        classifier_id: group.classifier?.classifier_id,
        user_ids: updatedUserIds,
        narrative_mapping_ids: group.narrative_mapping_ids || [],
      };
      
      updateGroupMutation.mutate({ groupId, groupData: updateData });
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Failed to assign persona to group');
    }
  }, [groups, updateGroupMutation]);

  const saveOnboardingConfig = useCallback(async (groupId: number, onboarding: OnboardingConfigFormData) => {
    try {
      if (onboarding.enabled) {
        await fetch(`/api/onboarding/${groupId}/configuration`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            genders: onboarding.genders,
            age_ranges: onboarding.age_ranges,
            ethnicities: onboarding.ethnicities,
            primary_languages: onboarding.primary_languages,
            supported_applications: onboarding.supported_applications,
            completion_redirect_url: onboarding.completion_redirect_url || null,
          }),
        });
      } else {
        await fetch(`/api/onboarding/${groupId}/configuration`, { method: 'DELETE' }).catch(() => {});
      }
    } catch (e) {
      console.error('Failed to save onboarding config:', e);
    }
  }, []);

  const handleSaveGroup = useCallback((groupData: CreateGroupRequest, onboarding: OnboardingConfigFormData) => {
    if (selectedGroup) {
      updateGroupMutation.mutate({ groupId: selectedGroup.group_id, groupData }, {
        onSuccess: (data: any) => {
          saveOnboardingConfig(selectedGroup.group_id, onboarding);
        },
      });
    } else {
      createGroupMutation.mutate(groupData, {
        onSuccess: (data: any) => {
          if (data?.group_id) {
            saveOnboardingConfig(data.group_id, onboarding);
          }
        },
      });
    }
  }, [selectedGroup, createGroupMutation, updateGroupMutation, saveOnboardingConfig]);

  const handleSavePersona = useCallback((personaData: CreatePersonaRequest) => {
    if (selectedPersona) {
      updatePersonaMutation.mutate({ userId: selectedPersona.user_id, personaData });
    } else {
      createPersonaMutation.mutate(personaData);
    }
  }, [selectedPersona, createPersonaMutation, updatePersonaMutation]);

  const getGroupStats = (group: PersonaGroup) => {
    const regionCounts = group.users.reduce((acc, user) => {
      const region = user.region?.carrier_region || 'Unassigned';
      acc[region] = (acc[region] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(regionCounts).map(([region, count]) => `${region}: ${count}`).join(', ');
  };

  if (groupsLoading) return <div className="loading">Loading persona groups...</div>;
  if (groupsError) return <div className="error">Error loading groups: {(groupsError as Error).message}</div>;

  return (
    <div className="persona-groups">
      <div className="page-header">
        <h1>👥 Persona Groups</h1>
        <div className="page-actions">
          <button onClick={() => refetchGroups()} className="btn-secondary">
            🔄 Refresh
          </button>
          <button onClick={handleCreatePersona} className="btn-secondary">
            👤 Add Persona
          </button>
          <button onClick={handleCreateGroup} className="btn-primary">
            ➕ Add Group
          </button>
        </div>
      </div>

      {actionError && (
        <div className="error-banner">
          <span>⚠️ {actionError}</span>
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      <div className="groups-search-bar" style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="🔍 Search groups by name..."
          value={groupSearchFilter}
          onChange={(e) => setGroupSearchFilter(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.05)',
            color: 'white',
            fontSize: '14px'
          }}
        />
        {groupSearchFilter && (
          <span style={{ marginLeft: '12px', color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>
            Found {filteredAndSortedGroups.length} of {groups.length} groups
          </span>
        )}
      </div>

      <div className="groups-grid">
        {filteredAndSortedGroups.map((group) => (
          <div key={group.group_id} className="group-card">
                         <div className="group-header">
               <div className="group-title">
                 <h3>{group.name}</h3>
                 <span className="group-count">{group.users.length} personas</span>
               </div>
                             <div className="group-actions">
                 <button 
                   onClick={() => handleEditGroup(group)}
                   className="btn-icon"
                   title="Edit group"
                 >
                   ✏️
                 </button>
                 <button 
                   onClick={() => handleDeleteGroup(group)}
                   className="btn-icon btn-danger"
                   title="Delete group"
                 >
                   🗑️
                 </button>
               </div>
            </div>

            <div className="group-details">
              <div className="group-classifier">
                <strong>Classifier:</strong> {group.classifier ? (
                  <span className="classifier-assigned">
                    🔧 (ID: {group.classifier.classifier_id}) {group.classifier.classifier_type} - {group.classifier.classifier_params ? `(${JSON.stringify(group.classifier.classifier_params)})` : ''}
                  </span>
                ) : (
                  <span className="classifier-unassigned">⚠️ No classifier assigned</span>
                )}
              </div>

              <div className="group-narratives">
                <strong>Narratives:</strong> {group.narrative_mapping_ids && group.narrative_mapping_ids.length > 0 ? (
                  <span className="narratives-assigned">
                    📖 {group.narrative_mapping_ids.map(id => {
                      const narrative = narratives.find(n => n.narrative_id === id);
                      return narrative ? narrative.name : `ID ${id}`;
                    }).join(', ')}
                  </span>
                ) : (
                  <span className="narratives-unassigned">⚠️ No narratives assigned</span>
                )}
              </div>

              <div className="group-stats" style={{ marginBottom: '6px' }}>
                <strong>Onboarding:</strong> {onboardingConfigs[group.group_id] ? (
                  <span className="narratives-assigned">
                    {(onboardingConfigs[group.group_id].supported_applications || []).length > 0
                      ? (onboardingConfigs[group.group_id].supported_applications || []).map(pkg => appDisplayNames[pkg] || pkg).join(', ')
                      : 'Configured (no apps)'}
                  </span>
                ) : (
                  <span className="classifier-unassigned">Not configured</span>
                )}
              </div>

              <div className="group-stats">
                <strong>Regions:</strong> {getGroupStats(group) || 'No personas assigned'}
              </div>

              <div className="group-personas">
                                 {group.users.slice(0, 5).map((persona) => (
                   <div key={persona.user_id} className="persona-mini" onClick={() => handleEditPersona(persona)}>
                     <span className="persona-name">{persona.user_id}</span>
                    {persona.region && (
                      <span className="persona-region-flag">
                        {countryCodeToFlag(persona.region.carrier_region)}
                      </span>
                    )}
                  </div>
                ))}
                {group.users.length > 5 && (
                  <span className="personas-more" onClick={() => handleEditGroup(group)} style={{cursor: 'pointer', textDecoration: 'underline'}}>
                    +{group.users.length - 5} more (click to view all)
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredAndSortedGroups.length === 0 && groups.length > 0 && (
          <div className="empty-state">
            <h3>No groups match your search</h3>
            <p>Try adjusting your search term or clear the filter.</p>
            <button onClick={() => setGroupSearchFilter('')} className="btn-secondary">
              Clear Search
            </button>
          </div>
        )}
        {groups.length === 0 && (
          <div className="empty-state">
            <h3>No persona groups yet</h3>
            <p>Create your first group to organize personas for bot deployment.</p>
            <button onClick={handleCreateGroup} className="btn-primary">
              ➕ Create First Group
            </button>
          </div>
        )}
      </div>

      {unassignedPersonas.length > 0 && (
                <div className="unassigned-section">
          <h3>📋 Unassigned Personas ({unassignedPersonas.length})</h3>
          <div className="unassigned-personas">
            {unassignedPersonas.map((persona) => {
              const personaLabels = parseLabels(persona.labels);
              return (
                <div key={persona.user_id} className="persona-card compact">
                  <div className="persona-card-header">
                    <div className="persona-main-info">
                      <div className="persona-type-badge">
                        {persona.persona_type === 'bot' ? '🤖' : '👤'} {persona.persona_type}
                      </div>
                      {persona.region && (
                        <div className="persona-region-info">
                          <span className="region-flag">
                            {countryCodeToFlag(persona.region.carrier_region)}
                          </span>
                          <span className="region-name">{persona.region.carrier_region}</span>
                        </div>
                      )}
                    </div>
                    <div className="persona-actions">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPersona(persona);
                        }}
                        className="btn-icon edit"
                        title="Edit persona"
                      >
                        ✏️
                      </button>
                    </div>
                  </div>
                  
                  {persona.demographic && (
                    <div className="persona-demographic compact">
                      <strong>Demo:</strong> {persona.demographic}
                    </div>
                  )}

                  {persona.device_info && (
                    <div className="persona-device-info compact">
                      <strong>Device:</strong> {persona.device_info.device_type} {persona.device_info.device_brand} {persona.device_info.device_platform} v{persona.device_info.device_os_version}
                    </div>
                  )}
                  
                  {personaLabels.length > 0 && (
                    <div className="persona-labels compact">
                      <strong>Labels:</strong>
                      <div className="labels-list">
                        {personaLabels.slice(0, 3).map((label, index) => (
                          <span key={index} className="label-tag mini">{label}</span>
                        ))}
                        {personaLabels.length > 3 && (
                          <span className="label-more">+{personaLabels.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="persona-id">
                    <small>ID: {persona.user_id.slice(0, 8)}...</small>
                  </div>
                  
                  <div className="persona-assign-section">
                    <div className="assign-controls">
                      <select
                        className="assign-group-select"
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAssignPersonaToGroup(persona.user_id, parseInt(e.target.value));
                            e.target.value = ""; // Reset selection
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">📋 Assign to group...</option>
                        {groups.map((group) => (
                          <option key={group.group_id} value={group.group_id}>
                            👥 {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <GroupModal
        group={selectedGroup}
        onboardingConfig={selectedOnboardingConfig}
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        onSave={handleSaveGroup}
        isLoading={createGroupMutation.isPending || updateGroupMutation.isPending}
        classifiers={classifiers}
        availablePersonas={availablePersonas}
        proxies={proxies}
        narratives={narratives}
        onEditPersona={handleEditPersona}
      />

      <PersonaModal
        persona={selectedPersona}
        isOpen={isPersonaModalOpen}
        onClose={() => setIsPersonaModalOpen(false)}
        onSave={handleSavePersona}
        isLoading={createPersonaMutation.isPending || updatePersonaMutation.isPending}
        regions={regions}
        devices={devices}
        proxies={proxies}
      />
    </div>
  );
};

export default PersonaGroups;
